import {
  EyeSlash,
  Flag,
  MagnifyingGlass,
  PencilSimple,
  PaperPlaneTilt,
  Trash,
  X,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Post } from '@sirocco/shared';
import { posts as postsApi, type FilDAccueil } from '../../api/endpoints';
import { PostCard } from '../../components/PostCard';
import { ScreenError, ScreenLoader, ServerScreen } from '../../components/layout/Screens';
import { EmptyState, Tabs } from '../../components/ui/Bits';
import { Cloche } from './Cloche';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { PhotoViewer } from '../../components/ui/PhotoViewer';
import { PartageSheet } from '../../components/ui/PartageSheet';
import { Sheet, SheetAction } from '../../components/ui/Sheet';
import { useAsync } from '../../hooks/useAsync';
import { useTempsDeLecture } from '../../pourtoi/lecture';
import {
  changerDeMode,
  modeDuFil,
  type ModeDuFil,
} from '../../pourtoi/preference';
import { useSession } from '../../store/SessionContext';
import { useFlashOnArrival } from '../../utils/flash';
import { lienDePost } from '../../utils/partage';
import { reportPath } from '../report/ReportScreen';
import { t } from '../../i18n';

/**
 * Le feed du spot : l'ecran d'accueil de l'application.
 *
 * Like et signet sont appliques immediatement a l'ecran puis confirmes
 * par le serveur. Si l'appel echoue on recharge, ce qui remet l'affichage
 * en accord avec la verite du serveur sans message d'erreur brutal.
 */
/** A quelle distance du bas la page suivante est demandee. */
const SEUIL = 400;
const MARGE = `${SEUIL}px`;

/** Les deux vues du fil, quand la ville publie assez pour qu'on ait a choisir. */
const VUES: { value: ModeDuFil; label: string }[] = [
  { value: 'pourtoi', label: t.fil.ecran.vues.pourToi },
  { value: 'recent', label: t.fil.ecran.vues.recent },
];

export function FeedScreen() {
  const { server, user } = useSession();
  const navigate = useNavigate();

  /*
    La recherche.

    Le texte est retarde avant de partir : sans cela, chaque frappe
    relirait le fil. Et une recherche lit tout le spot d'un coup — la
    base ne sait pas chercher dans du texte, il n'y a donc pas de page
    suivante a demander.
  */
  /*
    La recherche vit dans l'adresse.

    `/app/feed?q=rooftop` decrit un etat complet : ouvrir un post puis
    revenir en arriere ramene sur cette adresse, donc sur la recherche et
    non sur le fil entier. La position de defilement suit, elle aussi —
    elle est retenue par adresse.

    L'adresse est remplacee et non empilee : chercher n'est pas aller
    quelque part, et le retour arriere doit sortir du fil, pas defaire
    une recherche apres l'autre.
  */
  const [parametres, setParametres] = useSearchParams();
  const applied = parametres.get('q') ?? '';

  /*
    Ce qu'on tape vit a cote, et rejoint l'adresse apres un court silence.

    Le fil se met donc a jour pendant qu'on ecrit, sans relire le spot a
    chaque lettre. Le clavier, lui, ne bouge pas : c'est la bande
    d'onglet qui se remontait a chaque changement d'adresse et emportait
    le champ avec elle — voir `TabSwipe`.
  */
  const [query, setQuery] = useState(applied);
  const [searching, setSearching] = useState(applied.length > 0);
  /*
    Le clavier est-il a l'ecran. C'est cet etat, et non `document.
    activeElement`, qui commande le voile : il suit le champ au lieu de
    dependre du moment ou WebKit decide de deplacer le focus.
  */
  const [clavier, setClavier] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const zoneRef = useRef<HTMLDivElement>(null);

  /** Porte le terme dans l'adresse, ou l'en retire s'il est vide. */
  const appliquer = useCallback(
    (terme: string) => {
      setParametres(terme ? { q: terme } : {}, { replace: true });
    },
    [setParametres],
  );

  useEffect(() => {
    const voulu = query.trim();
    if (voulu === applied) return;
    const minuteur = setTimeout(() => appliquer(voulu), 300);
    return () => clearTimeout(minuteur);
  }, [query, applied, appliquer]);

  function openSearch() {
    setSearching(true);
    // Le clavier monte avec la barre, pas apres : le focus est pose
    // pendant que l'ouverture se joue.
    requestAnimationFrame(() => searchRef.current?.focus());
  }

  /** Chercher maintenant, et rendre l'ecran : le clavier redescend. */
  function lancer() {
    appliquer(query.trim());
    searchRef.current?.blur();
  }

  /*
    « Pour toi » ou « Récent ».

    Le choix ne parait que quand la ville publie plus qu'on ne lit en un
    passage : en dessous, le fil reste chronologique et il n'y a rien a
    choisir. Les suggestions n'ont plus d'interrupteur : elles valent pour
    tout le monde, et « Récent » reste le moyen de revenir a l'ordre des
    dates.
  */
  const [mode, setMode] = useState<ModeDuFil>(modeDuFil);

  const { data, loading, error, reload, refresh, setData } = useAsync<FilDAccueil>(
    () =>
      applied
        ? postsApi.feed(undefined, applied).then((page) => ({ ...page, dense: false, classe: false }))
        : postsApi.fil(mode),
    [applied, mode],
    /*
      Une entree de cache par recherche. Revenir d'un post retrouve donc
      ses resultats affiches, sans attendre le reseau — c'est ce qui rend
      le retour instantane plutot que clignotant.
    */
    `feed${applied && `:${applied}`}`,
    /*
      L'accueil ne se relit pas tout seul.

      Revenir d'un autre onglet remettait le fil a jour en silence, et le
      post qu'on etait en train de lire glissait sous d'autres, arrives
      entre-temps. On garde donc ce qui est affiche : c'est le geste de
      tirage, et lui seul, qui va chercher du neuf. Une exception a
      l'accueil — ailleurs, retrouver la liste fraiche est ce qu'on veut.
    */
    true,
  );
  const [menuPost, setMenuPost] = useState<Post | null>(null);
  /* Le post dont on ouvre la feuille de partage. Separe du menu : le menu
     se referme en la lancant, et elle doit survivre a sa fermeture. */
  const [partagePost, setPartagePost] = useState<Post | null>(null);
  /*
    Le bouton d'ou le menu sort. Une ref plutot qu'un etat : il n'entre
    dans aucun rendu, il n'est lu qu'a l'ouverture de la fenetre, et le
    ranger dans l'etat ferait un rendu de la liste entiere pour un noeud
    que personne n'affiche.
  */
  const menuAnchor = useRef<HTMLElement | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  // Un post supprime ne revient pas : la question se pose au centre.
  const { pending, request, cancel, confirm } = useConfirm();

  /*
    Le retour d'un ecran de signalement. L'ecran s'etant remonte entre
    temps, ses donnees sont fraiches : le post signale a deja disparu de
    la liste quand le message s'affiche.
  */
  useFlashOnArrival(flash);

  /** Ferme la feuille d'actions. */
  function closeMenu() {
    setMenuPost(null);
  }

  /** Referme la feuille, puis pose la question. Jamais les deux ensemble. */
  function ask(question: string, run: () => void) {
    closeMenu();
    request(question, run);
  }

  /*
    Le fil arrive par pages, et tout tient dans `data` : les posts deja
    charges comme le curseur de la suite.

    Les pages suivantes s'y ajoutent plutot que de vivre a cote. C'est ce
    qui fait qu'en revenant sur l'onglet on retrouve le fil ou on l'avait
    laisse — le cache d'ecran garde `data`, donc les trois pages lues, et
    non la seule premiere. Un etat separe aurait ramene a vingt posts, et
    la position de defilement restauree serait tombee dans le vide.
  */
  const [loadingMore, setLoadingMore] = useState(false);
  const feed = data?.posts ?? [];
  const cursor = data?.cursor ?? null;

  /*
    Le temps passe sur chaque post, quand le fil est classe pour moi.

    Il ne sert qu'a ce classement : il ne se mesure donc que la ou il
    s'applique, et reste sur le telephone.
  */
  const filRef = useRef<HTMLDivElement>(null);
  const parId = useRef(new Map<string, Post>());
  parId.current = new Map(feed.map((post) => [post.id, post]));
  useTempsDeLecture(
    filRef,
    Boolean(data?.classe) && !applied,
    feed.map((post) => post.id).join(),
    (postId, niveau) => {
      const post = parId.current.get(postId);
      if (post) postsApi.lecture(post, niveau);
    },
  );

  /** Remplace un post dans la liste, sans toucher aux autres. */
  function replace(updated: Post) {
    setData((previous) => ({
      ...previous,
      posts: previous.posts.map((post) => (post.id === updated.id ? updated : post)),
    }));
  }

  /** Retire un post du fil affiche. */
  function drop(postId: string) {
    setData((previous) => ({
      ...previous,
      posts: previous.posts.filter((post) => post.id !== postId),
    }));
  }

  /** La suite du fil, demandee a l'approche du bas. */
  async function loadMore() {
    if (loadingMore || !cursor) return;
    setLoadingMore(true);
    try {
      const page = await postsApi.feed(cursor, applied);
      setData((previous) => ({
        ...previous,
        posts: [...previous.posts, ...page.posts],
        cursor: page.cursor,
      }));
    } catch {
      // Un echec ne vide rien : le fil deja charge reste lisible, et la
      // sentinelle redemandera au prochain passage.
    } finally {
      setLoadingMore(false);
    }
  }

  async function onLike(post: Post) {
    replace({
      ...post,
      liked: !post.liked,
      likeCount: post.likeCount + (post.liked ? -1 : 1),
    });
    try {
      replace(await postsApi.like(post));
    } catch {
      reload();
    }
  }

  async function onBookmark(post: Post) {
    replace({ ...post, bookmarked: !post.bookmarked });
    try {
      replace(await postsApi.bookmark(post));
    } catch {
      reload();
    }
  }

  async function onHide(post: Post) {
    closeMenu();
    drop(post.id);
    try {
      await postsApi.hide(post.id, post);
    } catch {
      reload();
    }
  }

  async function onDelete(post: Post) {
    closeMenu();
    drop(post.id);
    try {
      await postsApi.remove(post);
      flash(t.fil.ecran.supprime);
    } catch {
      reload();
      flash(t.fil.commun.suppressionImpossible);
    }
  }

  /**
   * Ouvre la feuille de partage.
   *
   * Les amis de Sirocco d'abord, le reste du telephone en dessous : ce
   * qu'on partage le plus souvent va a quelqu'un qui est deja la, et lui
   * ouvrir une autre application pour lui envoyer un lien qui le ramene
   * ici n'a pas de sens.
   */
  function onShare(post: Post) {
    closeMenu();
    setPartagePost(post);
  }

  /**
   * Ouvre le fil de commentaires.
   * Le post entier part avec la navigation : l'ecran suivant le rappelle
   * en tete, like et signet compris, sans avoir a le relire dans la base.
   */
  function openComments(post: Post, compose = false) {
    navigate(`/app/feed/${post.id}/comments`, { state: { post, compose } });
  }

  /** Petit message ephemere en bas de l'ecran. */
  function flash(message: string) {
    setToast(message);
    setTimeout(() => setToast(null), 2600);
  }

  const isMine = menuPost?.author.id === user?.id;

  /*
    La suite se demande a l'approche du bas, jamais par un bouton : le fil
    s'allonge pendant qu'on lit.

    Deux declencheurs pour une seule action. L'observateur est le bon
    outil — il ne coute rien tant que rien ne bouge — mais ses rappels
    sont suspendus quand la page n'est pas consideree visible, ce qui
    arrive dans une webview revenant d'arriere-plan. La position au
    defilement, elle, repond toujours. `loadMore` se garde des appels en
    double, les deux peuvent donc tirer ensemble sans dommage.
  */
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const cible = sentinel.current;
    if (!cible) return;

    const observateur = new IntersectionObserver(
      (entrees) => {
        if (entrees.some((entree) => entree.isIntersecting)) void loadMore();
      },
      { rootMargin: MARGE },
    );
    observateur.observe(cible);

    const zone = cible.closest('.scroll-area');
    const auDefilement = () => {
      if (!zone) return;
      if (zone.scrollHeight - zone.scrollTop - zone.clientHeight < SEUIL) void loadMore();
    };
    zone?.addEventListener('scroll', auDefilement, { passive: true });

    return () => {
      observateur.disconnect();
      zone?.removeEventListener('scroll', auDefilement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, loadingMore]);

  return (
    <ServerScreen
      onRefresh={refresh}
      /* Le nom de la ville et la recherche ne bougent pas : c'est le fil
         qu'on tire, et ces deux-la disent ou l'on est. */
      sticky={
        <>
          {/*
        Le nom de la ville, sa barre, et la recherche.

        Une seule transition anime tout : c'est la barre qui s'ouvre et
        pousse le bouton. Le bouton n'a aucune position propre — il est
        cale a droite par une marge automatique, que la barre mange en
        grandissant. Rien a mesurer, rien a recalculer.
      */}
          <div className={`feed__head${searching ? ' feed__head--open' : ''}`}>
            {/* Le nom court : on est deja dans le spot, son perimetre a
                ete lu au moment de le choisir et n'a plus a etre repete
                en tete de chaque fil. */}
            <h1 className="feed__title">
              {server?.shortName ?? server?.name ?? 'Sirocco'}
              <span className="feed__rule" aria-hidden="true" />
            </h1>

            {/*
          La zone que le bouton traverse. Elle occupe tout l'espace laisse
          libre par le nom de la ville, et sert de repere aux deux
          mouvements : la loupe va de son bord droit a son bord gauche, la
          barre s'ouvre derriere elle et finit par la recouvrir.
        */}
            <div className="feed__zone" ref={zoneRef}>
              <div className="feed__search">
                <input
                  ref={searchRef}
                  type="search"
                  className="feed__search-input"
                  placeholder={t.fil.ecran.rechercher}
                  value={query}
                  /* La touche du clavier annonce ce qu'elle fera. */
                  enterKeyHint="search"
                  /* Hors recherche, le champ sort du parcours au clavier. */
                  tabIndex={searching ? 0 : -1}
                  aria-hidden={!searching}
                  onFocus={() => setClavier(true)}
                  onBlur={() => setClavier(false)}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    lancer();
                  }}
                />

                {/*
              La croix rend le fil entier : elle efface le terme, referme
              la barre et rend l'ecran. C'est la seule sortie de la
              recherche, elle reste donc la tant que la barre est ouverte
              — le doigt pose ailleurs, lui, ne fait plus que baisser le
              clavier.

              Elle ne prend pas le focus au champ pendant l'appui : sans
              cela, le clavier remonterait avant de redescendre.
            */}
                {searching && (
                  <button
                    type="button"
                    className="feed__search-clear"
                    aria-label={t.composants.champ.effacer}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setQuery('');
                      appliquer('');
                      setSearching(false);
                      searchRef.current?.blur();
                    }}
                  >
                    <X size={15} weight="bold" />
                  </button>
                )}
              </div>

              {/*
            Le bouton ferme. Il s'efface une fois la barre ouverte — la
            loupe qu'il portait se retrouve exactement la, dans la barre,
            de sorte que l'oeil suit un seul objet d'un bout a l'autre.
            On ne sort plus par un bouton mais en touchant ailleurs.
          */}
              {/* La cloche, sans bulle : elle vit a cote de la loupe, pas
                  dans un bouton de plus. */}
              <Cloche />
              <button
                type="button"
                className="feed__search-btn"
                aria-label={t.fil.ecran.rechercherLabel}
                aria-expanded={searching}
                tabIndex={searching ? -1 : 0}
                onClick={() => openSearch()}
              >
                <MagnifyingGlass size={19} weight="bold" />
              </button>
            </div>
          </div>
        </>
      }
      footer={
        <button
          className="fab"
          aria-label={t.fil.ecran.ecrire}
          onClick={() => navigate('/app/feed/compose')}
        >
          <PencilSimple size={26} weight="regular" />
        </button>
      }
      overlay={
        <>
          {/*
            Le message passe par la fente `overlay`, jamais par le contenu.

            Pose dans les enfants, il s'ancrait a `.server-screen__body`,
            qui est positionne et ne fait que la hauteur de ce qu'il
            contient : sur un feed d'un seul post, « au-dessus de la barre
            d'onglets » tombait juste sous ce post, en haut de l'ecran.
            Meme cause que la feuille d'actions d'un `SubScreen`.
          */}
          {toast && <p className="feed__toast">{toast}</p>}
          {/*
            Le voile du clavier.

            Tant que le clavier est la, le premier appui hors de la barre
            ne doit rien faire d'autre que le rendre : sans ce voile, le
            doigt pose sur un post l'ouvrait, et celui pose sur un visage
            ouvrait un profil. Retenir le clic apres coup marchait dans un
            navigateur mais dependait d'un ordre d'evenements que WebKit
            n'a pas promis.

            Le voile, lui, ne laisse aucun doute : il recoit l'appui, rend
            l'ecran, et disparait. Ce qu'on visait se touche ensuite. La
            barre de recherche passe par-dessus — elle reste donc utilisable.
          */}
          {clavier && (
            <div
              className="feed__veil"
              aria-hidden="true"
              onPointerDown={() => searchRef.current?.blur()}
            />
          )}

          <PhotoViewer src={photo} onClose={() => setPhoto(null)} />
          {/*
          La fenetre est posee au niveau de l'ecran, pas dans la zone
          defilante : son voile couvre ainsi l'illustration jusqu'en haut.
        */}
          <Sheet open={menuPost !== null} anchor={menuAnchor} onClose={closeMenu}>
            {menuPost && (
              <>
                <SheetAction
                  icon={<PaperPlaneTilt className="sens-x" size={22} />}
                  onClick={() => onShare(menuPost)}
                >
                  {t.fil.commun.partager}
                </SheetAction>

                {/* Un membre ne masque ni ne signale ses propres posts : il les supprime. */}
                {isMine ? (
                  <SheetAction
                    icon={<Trash size={22} />}
                    tone="danger"
                    onClick={() => ask(t.fil.commun.supprimerPostQ, () => void onDelete(menuPost))}
                  >
                    {t.fil.commun.supprimerPost}
                  </SheetAction>
                ) : (
                  <SheetAction
                    icon={<EyeSlash size={22} />}
                    onClick={() => ask(t.fil.ecran.masquerQ, () => void onHide(menuPost))}
                  >
                    {t.fil.ecran.masquer}
                  </SheetAction>
                )}

                {/* Le motif et le detail se choisissent sur leur propre ecran. */}
                {!isMine && (
                  <SheetAction
                    icon={<Flag size={22} />}
                    onClick={() =>
                      navigate(reportPath('post', menuPost.id, { serverId: menuPost.serverId }))
                    }
                  >
                    {t.fil.commun.signaler}
                  </SheetAction>
                )}
              </>
            )}
          </Sheet>

          {/*
            La feuille de partage est montee a cote du menu, pas dedans :
            le menu se referme en la lancant.
          */}
          <PartageSheet
            open={partagePost !== null}
            anchor={menuAnchor}
            onClose={() => setPartagePost(null)}
            quoi={partagePost ? { postId: partagePost.id } : null}
            lien={partagePost ? lienDePost(partagePost.id) : ''}
            texte={partagePost?.content || undefined}
            onFait={flash}
          />

          <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />
        </>
      }
    >
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}

      {data && feed.length === 0 && (
        /*
          Une recherche sans resultat n'est pas un fil vide.

          « Sois le premier a lancer une discussion » invitait a ecrire un
          post alors qu'il y en a peut-etre cent : ils ne repondent
          simplement pas a ce qu'on cherche. Proposer d'en ecrire un de
          plus ne repare rien.
        */
        applied.length > 0 ? (
          <EmptyState
            title={t.fil.ecran.aucunResultat}
            hint={t.fil.ecran.rienNeCorrespond(applied)}
          />
        ) : (
          <EmptyState
            title={t.fil.ecran.vide}
            hint={t.fil.ecran.videAide}
            action={{
              label: t.fil.ecran.premierPost,
              onClick: () => navigate('/app/feed/compose'),
            }}
          />
        )
      )}

      {/* Le choix de la vue, seulement quand il y a de quoi choisir. */}
      {data?.dense && !applied && (
        <div className="feed__vues">
          <Tabs
            value={mode}
            options={VUES}
            onChange={(suivant) => {
              changerDeMode(suivant);
              setMode(suivant);
            }}
          />
        </div>
      )}

      <div ref={filRef} className="feed__posts">
        {feed.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            onLike={onLike}
            onBookmark={onBookmark}
            onMenu={(picked, trigger) => {
              menuAnchor.current = trigger;
              setMenuPost(picked);
            }}
            onOpenComments={openComments}
            onOpenPhoto={setPhoto}
          />
        ))}
      </div>

      {/*
        La sentinelle. Elle est posee sous le dernier post : quand elle
        entre dans le champ, la page suivante part. Rien a cliquer, et
        rien qui bouge sous le doigt.
      */}
      {cursor && <div ref={sentinel} className="feed__more" aria-hidden="true" />}
    </ServerScreen>
  );
}

